import React, { useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { Dialog, Tabs, Tooltip, Hanger } from '@economic/taco';
import { TwoFactorAuthAgreementEnforcementReason } from '~/payment-cards-feature';
import { useFeatureFlags } from '../../../../../shared/featureFlags';
import { Column, Row } from '../../../../../shared/components/Grid/Grid';
import { Field, Form } from '../../../../../shared/components/Form';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import ButtonCancelSubmit from '../../../../../shared/components/ui/buttons/ButtonCancelSubmit';
import { SuperUserConfirmationDialog } from '../SuperUserConfirmationDialog';
import { NonPayingSuperUserManageUsersConfirmationDialog } from '../NonPayingSuperUserManageUsersConfirmationDialog';
import OtherCompanyInfoContent from './OtherCompanyInfoContent';
import validationSchema from './validationSchema';

enum EditMasterInformationTab {
    MasterInformation = 'tab-master-information',
    Other = 'tab-other',
}

export default function EditCompanyInfoDialog({
    initialData,
    onSubmitted,
    isAdministrator,
    isCvrReadonly,
    isInternalAgreement,
    isAdministratorPayingForCurrentAgreement,
    enforce2FAReason,
    shouldDisable2FA,
    onClose,
    ...dialogProps
}: {
    initialData;
    onSubmitted?;
    isAdministrator;
    isCvrReadonly;
    isInternalAgreement;
    isAdministratorPayingForCurrentAgreement;
    enforce2FAReason;
    shouldDisable2FA;
    onClose?;
}) {
    const [formValues, setFormValues] = useState(initialData);
    const [showSuperUserConfirmationDialog, setShowSuperUserConfirmationDialog] = useState<boolean>(false);
    const [showNonPayingSuperUserManageUsersConfirmationDialog, setShowNonPayingSuperUserManageUsersConfirmationDialog] =
        useState<boolean>(false);
    // used to display dialog only once when checkbox is enabled / disabled multiple times
    const [hasSuperUserConfirmationDialogBeenShown, setHasSuperUserToggleConfirmationDialogBeenShown] = useState<boolean>(false);
    const [
        hasNonPayingSuperUserManageUsersConfirmationDialogDialogBeenShown,
        setHasNonPayingSuperUserManageUsersConfirmationDialogDialogBeenShown,
    ] = useState<boolean>(false);

    const [showHanger, setShowHanger] = useState(false);

    const features = useFeatureFlags();
    const disableEnforce2FACheckbox = features.isEnabled('disableEnforce2FACheckbox') && shouldDisable2FA;
    const canNonPayingSuperUserManageUsers = features.isEnabled('canNonPayingSuperUserManageUsers');

    async function handleSubmit(values) {
        return isAdministrator
            ? internalApiClient.post('administrator/updateCurrentAdministratorOffice', values)
            : internalApiClient.post('company/updateCurrentCompany', values);
    }

    const handleOnChange = values => {
        if (JSON.stringify(initialData) === JSON.stringify(values)) {
            return;
        }

        if (isAdministrator) {
            // canAdministratorToggleModulesSuperUser
            if (
                initialData.canAdministratorToggleModulesSuperUser !== values.canAdministratorToggleModulesSuperUser &&
                values.canAdministratorToggleModulesSuperUser &&
                !showSuperUserConfirmationDialog &&
                !hasSuperUserConfirmationDialogBeenShown
            ) {
                setShowSuperUserConfirmationDialog(true);
                setHasSuperUserToggleConfirmationDialogBeenShown(true);
            }
        } else if (isAdministratorPayingForCurrentAgreement) {
            // canCompanyToggleModulesSuperUser
            if (
                initialData.canCompanyToggleModulesSuperUser !== values.canCompanyToggleModulesSuperUser &&
                values.canCompanyToggleModulesSuperUser &&
                !showSuperUserConfirmationDialog &&
                !hasSuperUserConfirmationDialogBeenShown
            ) {
                setShowSuperUserConfirmationDialog(true);
                setHasSuperUserToggleConfirmationDialogBeenShown(true);
            }
        }

        if (isAdministrator || isAdministratorPayingForCurrentAgreement) {
            // canNonPayingSuperUserManageUsers
            if (
                initialData.canNonPayingSuperUserManageUsers !== values.canNonPayingSuperUserManageUsers &&
                values.canNonPayingSuperUserManageUsers &&
                !showNonPayingSuperUserManageUsersConfirmationDialog &&
                !hasNonPayingSuperUserManageUsersConfirmationDialogDialogBeenShown
            ) {
                setShowNonPayingSuperUserManageUsersConfirmationDialog(true);
                setHasNonPayingSuperUserManageUsersConfirmationDialogDialogBeenShown(true);
            }
        }

        setFormValues(values);
    };

    const onSuperUserConfirmationDialogClose = () => {
        setShowSuperUserConfirmationDialog(false);
        setHasSuperUserToggleConfirmationDialogBeenShown(true);

        isAdministrator
            ? setFormValues({ ...formValues, canAdministratorToggleModulesSuperUser: false })
            : setFormValues({ ...formValues, canCompanyToggleModulesSuperUser: false });
    };

    const onSuperUserConfirmationDialogConfirm = () => {
        setShowSuperUserConfirmationDialog(false);
        setHasSuperUserToggleConfirmationDialogBeenShown(true);
    };

    const onNonPayingSuperUserManageUsersConfirmationDialogClose = () => {
        setShowNonPayingSuperUserManageUsersConfirmationDialog(false);
        setFormValues({ ...formValues, canNonPayingSuperUserManageUsers: false });
    };

    const onNonPayingSuperUserManageUsersConfirmationDialogConfirm = () => {
        setShowNonPayingSuperUserManageUsersConfirmationDialog(false);
        setHasNonPayingSuperUserManageUsersConfirmationDialogDialogBeenShown(true);
    };

    function getEnforce2FAReasonText(enforce2FAReason: TwoFactorAuthAgreementEnforcementReason) {
        switch (enforce2FAReason) {
            case TwoFactorAuthAgreementEnforcementReason.EnforcedOnNewlyCreatedAgreement:
                return t`2FA cannot be disabled. Contact our support for more information.`;
            case TwoFactorAuthAgreementEnforcementReason.EnforcedByRegularUser:
                return t`Once enforced, 2FA cannot be disabled. Contact our support for more information.`;
            case TwoFactorAuthAgreementEnforcementReason.EnforcedByAdminUser:
                return t`2FA was enforced on your agreement by your Administrator. Contact them for more information.`;
            case TwoFactorAuthAgreementEnforcementReason.EnforcedOnRolledOutSegment:
                return t`2FA was enforced on your agreement by e-conomic. Contact our support for more information.`;
            default:
                return t`Enforce two factor authentication`;
        }
    }

    return (
        <Dialog onClose={onClose} size="md" {...dialogProps}>
            <Dialog.Content aria-label="company-info-edit">
                {({ close }) => (
                    <Tabs defaultId={EditMasterInformationTab.MasterInformation}>
                        <Tabs.List>
                            <Tabs.Trigger id={EditMasterInformationTab.MasterInformation}>
                                <Trans>Master information</Trans>
                            </Tabs.Trigger>
                            <Tabs.Trigger id={EditMasterInformationTab.Other}>
                                <Trans>Other</Trans>
                            </Tabs.Trigger>
                        </Tabs.List>
                        <Tabs.Content id={EditMasterInformationTab.MasterInformation}>
                            <Form
                                initialData={formValues}
                                onSubmit={handleSubmit}
                                onSubmitted={() => {
                                    close();
                                    onSubmitted?.(formValues);
                                }}
                                onChange={values => handleOnChange(values.values)}
                                validationSchema={() => validationSchema(isAdministrator, isInternalAgreement, formValues)}
                                skipNotifications>
                                {() => {
                                    return (
                                        <>
                                            <Dialog.Title>{t`Name – address – contact`}</Dialog.Title>
                                            <Row divided>
                                                <Column>
                                                    <Field.Text label={t`Name`} name="name" required />
                                                    <Field.Text label={t`Address 1`} name="address1" required />
                                                    <Field.Text label={t`Address 2`} name="address2" />
                                                    <Field.Text label={t`Postcode`} name="zipCode" required />
                                                    <Field.Text label={t`Town/City`} name="city" required />
                                                </Column>
                                                <Column>
                                                    <Field.Text label={t`Country`} name="country" />
                                                    <Field.Text label={t`Telephone`} name="phone" />
                                                    <Field.Text label={t`Fax`} name="fax" />
                                                    <Field.Text label={t`Mobile`} name="mobilePhone" />
                                                </Column>
                                                <Column>
                                                    <Field.Text
                                                        label={t`Contact`}
                                                        name="contact"
                                                        required={!isInternalAgreement}
                                                    />
                                                    <Field.Text
                                                        label={t`Contact e-mail`}
                                                        name="email"
                                                        required={!isInternalAgreement}
                                                    />
                                                    <Field.Text label={t`Website`} name="homepage" />
                                                </Column>
                                                <Column>
                                                    <Field.Text
                                                        label={t`E-mail e-conomic invoice to`}
                                                        name="invoiceToEmail"
                                                        required={!isInternalAgreement}
                                                    />
                                                    {!isAdministrator && (
                                                        <Field.Text label={t`Co. reg. no.`} name="cvr" disabled={isCvrReadonly} />
                                                    )}
                                                    {isAdministrator && <Field.Text label={t`Link text`} name="linkText" />}
                                                    <Tooltip title={getEnforce2FAReasonText(enforce2FAReason)} placement="right">
                                                        {initialData?.enforce2FA && (
                                                            <div>
                                                                <Field.Checkbox
                                                                    disabled={disableEnforce2FACheckbox}
                                                                    text={t`Enforce 2 factor authentication`}
                                                                    name="enforce2FA"
                                                                />
                                                            </div>
                                                        )}
                                                    </Tooltip>
                                                    <div>
                                                        {!initialData?.enforce2FA && showHanger && (
                                                            <Hanger>
                                                                <Hanger.Anchor>
                                                                    <div onMouseLeave={() => setShowHanger(false)}>
                                                                        <Field.Checkbox
                                                                            text={t`Enforce 2 factor authentication`}
                                                                            name="enforce2FA"
                                                                        />
                                                                    </div>
                                                                </Hanger.Anchor>
                                                                <Hanger.Content placement="right">{t`When enforcing 2FA, your users will need to set up 2FA on their account to access e-conomic. Once this option is activated, it can't be disabled.`}</Hanger.Content>
                                                            </Hanger>
                                                        )}
                                                        {!initialData?.enforce2FA && !showHanger && (
                                                            <div onMouseOver={() => setShowHanger(true)}>
                                                                <Field.Checkbox
                                                                    text={t`Enforce 2 factor authentication`}
                                                                    name="enforce2FA"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    {!isAdministrator && (
                                                        <Field.Checkbox
                                                            text={t`Anonymize customer data (GDPR)`}
                                                            name="enableCustomerAnonymization"
                                                        />
                                                    )}
                                                    {isAdministrator && (
                                                        <Field.Checkbox
                                                            text={t`Always allow Super Users to add, update or remove add-ons`}
                                                            name="canAdministratorToggleModulesSuperUser"
                                                        />
                                                    )}
                                                    {!isAdministrator && isAdministratorPayingForCurrentAgreement && (
                                                        <Field.Checkbox
                                                            text={t`Always allow the Super User to add, update or remove add-ons`}
                                                            name="canCompanyToggleModulesSuperUser"
                                                        />
                                                    )}
                                                    {showSuperUserConfirmationDialog && (
                                                        <SuperUserConfirmationDialog
                                                            open={showSuperUserConfirmationDialog}
                                                            onClose={onSuperUserConfirmationDialogClose}
                                                            onConfirm={onSuperUserConfirmationDialogConfirm}
                                                            isAdministrator={isAdministrator}
                                                        />
                                                    )}
                                                    {canNonPayingSuperUserManageUsers && isAdministrator && (
                                                        <div>
                                                            <Field.Checkbox
                                                                text={t`Always allow Super Users to add, update or remove users`}
                                                                name="canNonPayingSuperUserManageUsers"
                                                            />
                                                        </div>
                                                    )}
                                                    {canNonPayingSuperUserManageUsers &&
                                                        !isAdministrator &&
                                                        isAdministratorPayingForCurrentAgreement && (
                                                            <div>
                                                                <Field.Checkbox
                                                                    text={t`Always allow the Super User to add, update or remove users`}
                                                                    name="canNonPayingSuperUserManageUsers"
                                                                />
                                                            </div>
                                                        )}
                                                    {showNonPayingSuperUserManageUsersConfirmationDialog && (
                                                        <NonPayingSuperUserManageUsersConfirmationDialog
                                                            open={showNonPayingSuperUserManageUsersConfirmationDialog}
                                                            onClose={onNonPayingSuperUserManageUsersConfirmationDialogClose}
                                                            onConfirm={onNonPayingSuperUserManageUsersConfirmationDialogConfirm}
                                                            isAdministrator={isAdministrator}
                                                        />
                                                    )}
                                                </Column>
                                            </Row>
                                            <Dialog.Footer>
                                                <ButtonCancelSubmit
                                                    onCancel={() => {
                                                        onClose?.();
                                                        close();
                                                    }}
                                                    submitText={t`Save`}
                                                />
                                            </Dialog.Footer>
                                        </>
                                    );
                                }}
                            </Form>
                        </Tabs.Content>
                        <Tabs.Content id={EditMasterInformationTab.Other}>
                            <OtherCompanyInfoContent
                                closeDialog={() => {
                                    onClose?.();
                                    onSubmitted();
                                    close();
                                }}
                            />
                        </Tabs.Content>
                    </Tabs>
                )}
            </Dialog.Content>
        </Dialog>
    );
}
